import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useContent } from './ContentContext';

const AudioContext = createContext();

export const AudioProvider = ({ children }) => {
  const { content } = useContent();
  const audioRef = useRef(new Audio());
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(70);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(false);
  const shouldPlayRef = useRef(false); // Track if audio should be playing

  const music = content?.settings?.backgroundMusic;

  // Handle audio loading
  useEffect(() => {
    const audio = audioRef.current;

    // Clear audio and reset state if disabled or no URL
    if (!music?.enabled || !music?.url) {
      audio.pause();
      audio.src = '';
      setLoaded(false);
      setError(null);
      setPlaying(false);
      shouldPlayRef.current = false;
      return;
    }

    // Event handlers
    const handleLoadedData = () => {
      setLoaded(true);
      setError(null);
    };

    const handleError = (e) => {
      console.error('Audio load error:', e);
      setError('Failed to load audio');
      setLoaded(false);
    };

    // Add event listeners
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('error', handleError);

    // Set audio properties
    audio.src = music.url;
    audio.volume = (music.volume || 70) / 100;
    audio.loop = true;

    // Cleanup function
    return () => {
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('error', handleError);
      audio.pause();
    };
  }, [music?.url, music?.enabled, music?.volume]);

  // Handle page visibility (pause when tab is inactive)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const audio = audioRef.current;

      if (document.hidden) {
        // Tab is hidden - pause if playing
        if (playing && !audio.paused) {
          audio.pause();
          setPlaying(false);
        }
      } else {
        // Tab is visible - resume if should be playing
        if (shouldPlayRef.current && loaded && !muted) {
          audio.play()
            .then(() => setPlaying(true))
            .catch(() => setPlaying(false));
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [playing, loaded, muted]);

  // Auto-play on first user interaction (click/scroll/touch)
  // Browsers block autoplay without user gesture — this listens once
  const autoplayTriggeredRef = useRef(false);

  useEffect(() => {
    if (!loaded || !music?.enabled || autoplayTriggeredRef.current) return;

    const triggerAutoplay = () => {
      if (autoplayTriggeredRef.current) return;
      autoplayTriggeredRef.current = true;

      shouldPlayRef.current = true;
      audioRef.current.play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));

      document.removeEventListener('click', triggerAutoplay);
      document.removeEventListener('scroll', triggerAutoplay);
      document.removeEventListener('touchstart', triggerAutoplay);
    };

    document.addEventListener('click', triggerAutoplay, { once: true });
    document.addEventListener('scroll', triggerAutoplay, { once: true });
    document.addEventListener('touchstart', triggerAutoplay, { once: true });

    return () => {
      document.removeEventListener('click', triggerAutoplay);
      document.removeEventListener('scroll', triggerAutoplay);
      document.removeEventListener('touchstart', triggerAutoplay);
    };
  }, [loaded, music?.enabled]);

  const play = () => {
    if (!loaded || muted) return;

    autoplayTriggeredRef.current = true;
    shouldPlayRef.current = true;
    audioRef.current.play()
      .then(() => {
        setPlaying(true);
      })
      .catch((err) => {
        console.log('Autoplay blocked:', err);
        setPlaying(false);
      });
  };

  const pause = () => {
    shouldPlayRef.current = false;
    audioRef.current.pause();
    setPlaying(false);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    const newMuted = !muted;
    setMuted(newMuted);

    if (newMuted) {
      // Mute - pause audio
      audio.pause();
      setPlaying(false);
      shouldPlayRef.current = false;
    } else {
      // Unmute - start playing
      if (loaded) {
        shouldPlayRef.current = true;
        audio.play()
          .then(() => setPlaying(true))
          .catch(() => setPlaying(false));
      }
    }
  };

  const changeVolume = (newVolume) => {
    const vol = Math.max(0, Math.min(100, newVolume));
    audioRef.current.volume = vol / 100;
    setVolume(vol);
  };

  return (
    <AudioContext.Provider value={{ playing, play, pause, volume, changeVolume, loaded, error, muted, toggleMute }}>
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};
